// @flow
import config from '../../../../../config';
import {
  CLEAR_CREATION_TEMPLATES,
  ERROR_CREATION_TEMPLATES,
  LOAD_MORE_CREATION_TEMPLATES,
  RECEIVE_CREATION_TEMPLATES,
  REQUEST_CREATION_TEMPLATES,
} from '../../../../ActionsConst';

import { buildAndShowErrorToast } from '../../../../core/actions/toast/ToastActions';
import { getProductSizes } from '../../../../services/product-type/ProductTypeService';

import apiRequest from '../../../../../stores/ApiService';
import toastMsg from '../../../../../data/toast';

import type { Action } from '../../../../ActionsConst';
import type { Dispatch } from '../settings/SettingsActions';
import type { ProductTemplates } from '../../../../../api/types/ProductTemplates';

export type ReceiveCreationTemplatesAction = Action & {
  templates: ProductTemplates,
};

export type ClearCreationTemplatesAction = Action;

export type Request = {
  apiUrl?: string,
  productTypeId?: string | null,
  searchTerm?: string,
  start?: number,
  total?: number,
  hasMore?: boolean,
};

const creationTemplatesPageSize = config.page.creationTemplates;

export const clearCreationTemplates = (): ClearCreationTemplatesAction => ({
  type: CLEAR_CREATION_TEMPLATES,
});

export const errorCreationTemplatesAction = (): { type: string, load: { isLoading: boolean, hasMore: boolean } } => ({
  type: ERROR_CREATION_TEMPLATES,
  load: {
    isLoading: false,
    hasMore: false,
  },
});

export const requestCreationTemplatesAction = (): { type: string, load: { isLoading: boolean } } => ({
  type: REQUEST_CREATION_TEMPLATES,
  load: {
    isLoading: true,
  },
});

export const receiveCreationTemplates = (templates: ProductTemplates): ReceiveCreationTemplatesAction => ({
  type: RECEIVE_CREATION_TEMPLATES,
  templates,
});

export const loadMoreCreationTemplatesAction = ({
  hasMore,
  total,
  start,
}: Request): { type: string, load: { isLoading: boolean, hasMore?: boolean, total?: number, start?: number } } => ({
  type: LOAD_MORE_CREATION_TEMPLATES,
  load: {
    isLoading: false,
    hasMore,
    total,
    start,
  },
});

const buildReceiveTemplatesProductsURI = ({ apiUrl, productTypeId, searchTerm, start }: Request): string => {
  let productSizeParam = '';
  let templateSearchParam = '';
  const from = start || 0;
  const productTypeSizes = getProductSizes(productTypeId);

  productTypeSizes.forEach((productSize, index) => {
    const separator = index === 0 ? '' : ',';
    productSizeParam += `${separator}${encodeURIComponent(productSize.id)}`;
  });

  if (searchTerm) {
    templateSearchParam = `&query=${searchTerm}`;
  }

  return `${apiUrl ||
    ''}/templates?from=${from}&size=${creationTemplatesPageSize}&productSize=${productSizeParam}${templateSearchParam}`;
};

// Used by PACT
export const templatesEndpointRequest = (request: Request) =>
  apiRequest({
    method: 'GET',
    url: buildReceiveTemplatesProductsURI(request),
  });

export const requestCreationTemplates = (request: Request) => {
  const { start = 0 } = request;

  return (dispatch: Dispatch) => {
    dispatch(requestCreationTemplatesAction());
    templatesEndpointRequest(request)
      .then((response) => {
        return response.isRedirected ? null : response.json();
      })
      .then((templateResponse) => {
        if (templateResponse !== null) {
          dispatch(
            loadMoreCreationTemplatesAction({
              hasMore: start + creationTemplatesPageSize <= parseInt(templateResponse.total, 10),
              total: templateResponse.total,
              start: start + creationTemplatesPageSize,
            }),
          );
          dispatch(receiveCreationTemplates(templateResponse.items));
        }
      })
      .catch((err) => {
        dispatch(errorCreationTemplatesAction());
        dispatch(buildAndShowErrorToast({ messageDescriptor: toastMsg.commonError })); // Oh oh… Houston we have a problem.
        throw new Error(`Loading product templates failed:: ${err}`);
      });
  };
};
